import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { getRole, watchAuth } from './firebase';
import type { AuthState } from './auth-types';

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    const unsubscribe = watchAuth((user) => {
      if (!user) {
        if (!cancelled) setState({ status: 'unauthenticated' });
        return;
      }
      // Resolve the role claim before reporting `authenticated`, so the access
      // gate never sees a transient roleless-but-signed-in state and bounces a
      // legitimate operator.
      void getRole().then((role) => {
        if (!cancelled) setState({ status: 'authenticated', user, role });
      });
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const state = useContext(AuthContext);
  if (!state) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return state;
}
